import React, { useEffect, useState } from 'react';
import { getAllProducts } from '../../../../services/product';
import axios from 'axios';
import { message, Button, Switch, Input, Avatar, Select, Empty, Card, Row, Col, Tag } from 'antd';
import { isAuthenticated } from '../../../../services/auth';
import UnifiedDashboard from '../../../../components/layout/UnifiedDashboard';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PlusOutlined, DeleteOutlined, SearchOutlined, BarsOutlined } from '@ant-design/icons';
import './SponsoredAdsAdmin.css';

const SponsoredAdsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [sponsored, setSponsored] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const API = process.env.REACT_APP_API;
  const { token } = isAuthenticated();

  useEffect(() => {
    loadProducts();
    loadSponsored();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await getAllProducts();
      setProducts(res.data);
    } catch (err) {
      message.error('Erro ao carregar produtos');
    }
  };

  const loadSponsored = async () => {
    try {
      const res = await axios.get(`${API}/admin/sponsored-ads`);
      setSponsored(res.data);
    } catch (err) {
      message.error('Erro ao carregar anúncios patrocinados');
    }
  };

  const handleAddSponsored = async (productId) => {
    setLoadingId(productId);
    try {
      const nextOrder = sponsored.length > 0 ? Math.max(...sponsored.map(s => s.order || 0)) + 1 : 1;
      await axios.post(
        `${API}/admin/sponsored-ads`,
        { productId, order: nextOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadSponsored();
      await loadProducts();
      message.success('Produto adicionado ao destaque!');
      setShowDrawer(false);
      setSearch('');
      setAuthorFilter(null);
    } catch (err) {
      message.error(err.response?.data?.error || 'Erro ao adicionar destaque');
    }
    setLoadingId(null);
  };

  const handleRemoveSponsored = async (id) => {
    setLoadingId(`remove-${id}`);
    try {
      await axios.delete(`${API}/admin/sponsored-ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success('Removido dos destaques!');
      await loadSponsored();
      await loadProducts();
    } catch (err) {
      message.error('Erro ao remover destaque');
    }
    setLoadingId(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(sponsored);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({ ...item, order: index + 1 }));
    setSponsored(updatedItems);

    const orderData = updatedItems.map((item) => ({ id: item._id, order: item.order }));
    try {
      await axios.put(
        `${API}/admin/sponsored-ads/reorder`,
        { order: orderData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Ordem atualizada!');
    } catch (err) {
      message.error('Erro ao atualizar ordem');
      await loadSponsored();
    }
  };

  const handleToggleActive = async (item) => {
    setLoadingId(`switch-${item._id}`);
    try {
      await axios.put(
        `${API}/admin/sponsored-ads/${item._id}`,
        { active: !item.active, order: item.order },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadSponsored();
      message.success(`Produto ${!item.active ? 'ativado' : 'desativado'}!`);
    } catch (err) {
      message.error('Erro ao atualizar status');
    }
    setLoadingId(null);
  };

  const normalizeText = (text = '') =>
    text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const availableProducts = products.filter(
    (p) => p.status === 'active' && !sponsored.some((s) => s.product && s.product._id === p._id)
  );

  const filteredProducts = availableProducts.filter(p => {
    if (!search) return true;
    
    let priceStr = '';
    if (search && /^[0-9.,R$\s]+$/.test(search)) {
      const cleanTerm = search.replace(/[^\d,]/g, '').replace(',', '.');
      const parsedPrice = parseFloat(cleanTerm);
      if (!isNaN(parsedPrice)) {
        priceStr = parsedPrice.toString();
      }
    } else if (search) {
      priceStr = search.replace(/\D/g, '');
    }

    const term = normalizeText(search);
    const matchName = p.name && normalizeText(p.name).includes(term);
    const matchDesc = p.description && normalizeText(p.description).includes(term);
    const matchPrice = p.price && priceStr && p.price.toString().includes(priceStr);
    
    return matchName || matchDesc || matchPrice;
  });

  return (
    <UnifiedDashboard>
      <div className="sponsored-ads-content">
        {/* Header Section */}
        <div className="sponsored-ads-header">
          <div>
            <h1 className="sponsored-ads-title">
              Anúncios <span className="sponsored-ads-title-highlight">Patrocinados</span>
            </h1>
            <p className="sponsored-ads-description">Gerencie produtos em destaque e reordene por drag-and-drop</p>
          </div>
          <Button 
            type="primary" 
            size="large"
            icon={<PlusOutlined />}
            onClick={() => setShowDrawer(true)}
            className="sponsored-ads-add-btn"
          >
            Adicionar Destaque
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="sponsored-ads-stats">
          <Card className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{sponsored.length}</p>
              <p className="stat-label">Em Destaque</p>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{sponsored.filter(s => s.active).length}</p>
              <p className="stat-label">Ativos</p>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{availableProducts.length}</p>
              <p className="stat-label">Disponíveis</p>
            </div>
          </Card>
        </div>

        {/* Drawer Overlay */}
        <div 
          className={`sponsored-ads-overlay ${showDrawer ? 'active' : ''}`} 
          onClick={() => setShowDrawer(false)} 
        />

        {/* Drawer */}
        <div className={`sponsored-ads-drawer ${showDrawer ? '' : 'sponsored-ads-drawer-closed'}`}>
          <div className="sponsored-ads-drawer-header">
            <h2 className="sponsored-ads-drawer-title">Selecionar Produto</h2>
            <Button 
              type="text" 
              onClick={() => setShowDrawer(false)} 
              className="sponsored-ads-close-btn"
            >
              <span className="sponsored-ads-close-icon">✕</span>
            </Button>
          </div>
          
          <div className="sponsored-ads-drawer-body">
            <Input
              placeholder="Buscar produto ou valor..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => {
                let val = e.target.value;
                if (/[a-zA-Z]/.test(val) && /^[0-9.,R$\s]+$/.test(search)) {
                  val = val.replace(/[\d.,R$\s]/g, '').trim();
                } else if (val && /^[0-9.,R$\s]+$/.test(val) && /\d/.test(val)) {
                  const onlyNumbers = val.replace(/\D/g, '');
                  if (onlyNumbers && parseInt(onlyNumbers, 10) > 0) {
                    const numValue = parseInt(onlyNumbers, 10) / 100;
                    val = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numValue);
                  } else {
                    val = '';
                  }
                }
                setSearch(val);
              }}
              className="sponsored-ads-search"
              allowClear
            />

            {filteredProducts.length === 0 ? (
              <Empty description="Nenhum produto disponível" />
            ) : (
              filteredProducts.map(item => (
                <Card key={item._id} className="sponsored-ads-list-item">
                  <Row align="middle" gutter={[12, 0]}>
                    <Col span={5}>
                      <Avatar
                        shape="square"
                        size={56}
                        src={item.images?.[0] ? `${API.replace('/api', '')}${item.images[0]}` : undefined}
                      />
                    </Col>
                    <Col span={13}>
                      <h4 className="sponsored-ads-product-title">{item.name}</h4>
                      <p className="sponsored-ads-product-author">Por: {item.author?.name || 'Desconhecido'}</p>
                    </Col>
                    <Col span={6} style={{ textAlign: 'right' }}>
                      <Button
                        type="primary"
                        size="small"
                        loading={loadingId === item._id}
                        onClick={() => handleAddSponsored(item._id)}
                        icon={<PlusOutlined />}
                      />
                    </Col>
                  </Row>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Sponsored List */}
        <Card className="sponsored-list-card">
          {sponsored.length === 0 ? (
            <Empty 
              description="Nenhum anúncio patrocinado" 
              style={{ paddingTop: '40px', paddingBottom: '40px' }}
            />
          ) : (
            <>
              <div className="sponsored-ads-alert">
                📍 Arraste pela alça para reordenar
              </div>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sponsored-list">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`sponsored-ads-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                    >
                      {sponsored.map((item, index) => (
                        <Draggable key={item._id} draggableId={item._id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`sponsored-ads-item ${snapshot.isDragging ? 'dragging' : ''} ${!item.active ? 'inactive' : ''}`}
                            >
                              <div 
                                className="sponsored-ads-item-drag-handle" 
                                {...provided.dragHandleProps}
                              >
                                <BarsOutlined />
                              </div>

                              <Avatar
                                shape="square"
                                size={80}
                                src={item.product?.images?.[0] ? `${API.replace('/api', '')}${item.product.images[0]}` : undefined}
                                className="sponsored-ads-item-avatar"
                              />

                              <div className="sponsored-ads-item-info">
                                <h4 className="sponsored-ads-item-name">
                                  {item.product?.name || 'Produto removido'}
                                </h4>
                                <p className="sponsored-ads-item-desc">
                                  {item.product?.author?.name || 'Desconhecido'}
                                </p>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  <Tag color="blue">Posição #{item.order}</Tag>
                                  <Tag color={item.active ? 'green' : 'red'}>
                                    {item.active ? 'Ativo' : 'Inativo'}
                                  </Tag>
                                </div>
                              </div>

                              <div className="sponsored-ads-item-actions">
                                <Switch
                                  checked={item.active}
                                  loading={loadingId === `switch-${item._id}`}
                                  onChange={() => handleToggleActive(item)}
                                />
                                <Button
                                  type="primary"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  loading={loadingId === `remove-${item._id}`}
                                  onClick={() => handleRemoveSponsored(item._id)}
                                  className="sponsored-ads-remove-btn"
                                >
                                  Remover
                                </Button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Card>
      </div>
    </UnifiedDashboard>
  );
};

export default SponsoredAdsAdmin;
